import React, { useEffect, useState } from "react";
import { api, formatApiError, uploadPhoto } from "@/lib/api";
import { ShieldCheck, Camera, IdCard, Check, Clock, AlertCircle, Upload } from "lucide-react";

const LEVEL_META = {
  contact: { icon: <ShieldCheck className="h-5 w-5" />, label: "Kontakt", desc: "Din e-mail er bekræftet." },
  selfie: { icon: <Camera className="h-5 w-5" />, label: "Selfie-verificering", desc: "Upload en tydelig selfie hvor dit ansigt er synligt. Vi sammenligner med dine profilfotos." },
  identity: { icon: <IdCard className="h-5 w-5" />, label: "ID-verificering", desc: "Upload et pas eller kørekort. Kun admin ser billedet — det slettes efter godkendelse." },
};

export default function Verification() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/verify/me"); setData(data); }
    catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (level, file) => {
    setBusy(level); setErr("");
    try {
      const { path } = await uploadPhoto(file);
      await api.post("/verify/submit", { level, upload_path: path });
      await load();
    } catch (e) { setErr(formatApiError(e)); }
    setBusy(null);
  };

  if (!data) return <div className="max-w-3xl mx-auto px-4 py-16 text-brown-500">{err || "Indlæser…"}</div>;

  const status = data.status || {};
  const pendingByLevel = Object.fromEntries((data.pending || []).map((v) => [v.level, v]));

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-7" data-testid="verify-page">
      <div className="eyebrow flex items-center gap-2"><ShieldCheck className="h-3 w-3" /> Verificering</div>
      <h1 className="mt-2 font-serif text-4xl text-olive-700">Byg tillid — bliv verificeret</h1>
      <p className="mt-3 text-brown-600 text-sm max-w-xl">
        Verificerede profiler får mere synlighed og skaber roligt sind hos alvorlige familier. Vi håndterer dine dokumenter privat.
      </p>

      {err && <div className="mt-4 text-destructive text-sm" data-testid="verify-error">{err}</div>}

      <div className="mt-8 space-y-3">
        {["contact", "selfie", "identity"].map((lvl) => (
          <VerifyRow
            key={lvl}
            level={lvl}
            done={!!status[lvl]}
            pending={!!pendingByLevel[lvl]}
            busy={busy === lvl}
            onFile={(f) => submit(lvl, f)}
          />
        ))}
      </div>
    </div>
  );
}

function VerifyRow({ level, done, pending, busy, onFile }) {
  const meta = LEVEL_META[level];
  const status = done ? "done" : pending ? "pending" : "todo";
  return (
    <div className="card-soft p-5 flex items-center justify-between gap-4 flex-wrap" data-testid={`verify-row-${level}`}>
      <div className="flex items-center gap-3">
        <div className={`h-11 w-11 rounded-full flex items-center justify-center ${done ? "bg-olive-100 text-olive-700" : pending ? "bg-gold-100 text-gold-600" : "bg-sand-100 text-brown-600"}`}>
          {meta.icon}
        </div>
        <div>
          <div className="font-medium text-olive-700">{meta.label}</div>
          <div className="text-xs text-brown-500">{meta.desc}</div>
        </div>
      </div>
      <div>
        {status === "done" && <span className="text-sm text-olive-600 flex items-center gap-1"><Check className="h-4 w-4" /> Godkendt</span>}
        {status === "pending" && <span className="text-sm text-gold-600 flex items-center gap-1"><Clock className="h-4 w-4" /> Under gennemgang</span>}
        {status === "todo" && level !== "contact" && (
          <label className={`btn-primary text-sm px-4 py-2 cursor-pointer ${busy ? "opacity-60 pointer-events-none" : ""}`} data-testid={`verify-upload-${level}`}>
            <Upload className="h-4 w-4" /> {busy ? "Uploader…" : "Upload"}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
          </label>
        )}
        {status === "todo" && level === "contact" && <span className="text-xs text-brown-500 flex items-center gap-1"><AlertCircle className="h-4 w-4" /> Automatisk</span>}
      </div>
    </div>
  );
}
